import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ScrollText, Swords, Map, Trophy, Star, Compass, Landmark,
  CheckCircle2, SkipForward, Sparkles, ShieldAlert, Coins, Flame,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";

const CAT_ICON = { skilling: Star, quests: ScrollText, diaries: Map, combat: Swords, raids: Landmark, collection: Trophy, clues: Compass };
const STORAGE_KEY = "ts_guest";

const safeDecode = (s) => { try { return decodeURIComponent(s); } catch { return s; } };
const dayStr = (offset = 0) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toISOString().slice(0, 10); };

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
};
const save = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
};

export default function GuestBoard() {
  const { rsn } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const gm = params.get("gm") || "main";

  const [state, setState] = useState(null); // {rsn, gamemode, combat_level, total_level, current, seen, done, points, streak, lastStreakDate}
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showExit, setShowExit] = useState(false);

  const persist = useCallback((next) => { setState(next); save(next); }, []);

  const fetchNext = useCallback(async (base) => {
    const seen = base.seen || [];
    const { data } = await api.get("/public/guest/next", {
      params: { rsn: base.rsn, gamemode: base.gamemode, done: seen.join("|") },
    });
    return { ...base, current: data.task, combat_level: data.combat_level, total_level: data.total_level };
  }, []);

  // Boot: resume matching guest board from storage, else start a fresh one.
  useEffect(() => {
    let alive = true;
    (async () => {
      const saved = load();
      if (saved && saved.rsn && saved.rsn.toLowerCase() === safeDecode(rsn).toLowerCase()) {
        setState(saved);
        return;
      }
      try {
        const { data } = await api.get("/public/guest/next", { params: { rsn: safeDecode(rsn), gamemode: gm } });
        if (!alive) return;
        const fresh = {
          rsn: data.rsn, gamemode: gm, combat_level: data.combat_level, total_level: data.total_level,
          current: data.task, seen: [], done: [], points: 0, streak: 0, lastStreakDate: null,
        };
        persist(fresh);
      } catch (e) {
        if (alive) setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load that account.");
      }
    })();
    return () => { alive = false; };
  }, [rsn, gm, persist]);

  // Exit-intent: when a guest with unsaved points moves to leave, nudge them to save (once per tab).
  useEffect(() => {
    if (!state || (state.points || 0) <= 0) return;
    const onLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem("ts_guest_exit")) {
        setShowExit(true);
        try { sessionStorage.setItem("ts_guest_exit", "1"); } catch (_) {}
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, [state]);

  const nextTask = async (base) => {
    setBusy(true);
    try {
      const next = await fetchNext(base);
      persist(next);
    } catch (e) {
      toast.error("Couldn't fetch the next task — try again.");
      persist(base);
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!state?.current || busy) return;
    setBusy(true);
    const task = state.current;
    try {
      const { data } = await api.post("/public/guest/verify", {
        rsn: state.rsn, gamemode: state.gamemode, meta: task.meta,
      });
      if (!data.ok) {
        toast.error(data.message || "Not done yet — go finish it, then verify!");
        setBusy(false);
        return;
      }
      toast.success(`✅ ${data.message} +${data.points} points banked (pending)`);
      const done = [...(state.done || []), {
        title: task.title, category: task.category, description: task.description, meta: task.meta,
      }];
      const seen = [...(state.seen || []), task.title];
      // Daily streak: +1 per calendar day with a completion (loss-aversion for return visits).
      const today = dayStr();
      let streak = state.streak || 0;
      if (state.lastStreakDate === today) { /* already counted today */ }
      else if (state.lastStreakDate === dayStr(-1)) streak += 1;
      else streak = 1;
      const base = { ...state, done, seen, points: (state.points || 0) + (data.points || 0), current: null, streak, lastStreakDate: today };
      await nextTask(base);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed.");
      setBusy(false);
    }
  };

  const skip = async () => {
    if (!state?.current || busy) return;
    const seen = [...(state.seen || []), state.current.title];
    await nextTask({ ...state, seen, current: null });
  };

  const goSignup = () => {
    // Keep ts_guest in storage so Auth can bank the pending tasks after registration.
    navigate(`/login?claim=${encodeURIComponent(state.rsn)}&gm=${encodeURIComponent(state.gamemode)}`);
  };

  if (error) {
    return (
      <div className="stone-bg App flex flex-col items-center px-4 py-10">
        <div className="w-full max-w-xl">
          <div className="flex flex-col items-center mb-6"><Logo variant="hero" /></div>
          <div className="osrs-panel p-6 text-center" data-testid="guest-error">
            <p className="osrs-shadow text-[#FF6666] mb-4">{error}</p>
            <Link to="/login" className="osrs-btn osrs-btn-green px-5 py-2 inline-block">Try your own account →</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="stone-bg App flex flex-col items-center justify-center px-4 py-20">
        <p className="osrs-shadow text-[#E6A519] text-center py-10">Loading your guest board...</p>
      </div>
    );
  }

  const Icon = state.current ? (CAT_ICON[state.current.category] || Star) : Star;
  const completed = (state.done || []).length;

  return (
    <div className="stone-bg App flex flex-col items-center px-4 py-8" data-testid="guest-board">
      <div className="w-full max-w-2xl">
        <div className="flex flex-col items-center mb-5"><Logo variant="hero" /></div>

        {/* Guest banner */}
        <div className="osrs-panel p-3 mb-4 flex items-center gap-2 border-[#5a4a1e]" data-testid="guest-banner">
          <ShieldAlert size={18} className="text-[#FFCF3F] shrink-0" />
          <p className="osrs-shadow text-[#E6A519] text-xs leading-snug">
            You're playing as a <span className="text-[#FFCF3F]">guest</span> — nothing is saved to an account yet.
            Your points vanish if you close this tab. <span className="text-[#7CFC7C]">Create a free account to bank them.</span>
          </p>
        </div>

        <div className="osrs-panel p-6">
          {/* Header + stats */}
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <h1 className="osrs-title text-2xl" data-testid="guest-rsn">{state.rsn}</h1>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 osrs-num text-[#FFCF3F] text-sm" data-testid="guest-pending-points">
                <Coins size={15} className="text-[#FFD700]" /> {state.points || 0} pts
              </span>
              <span className="inline-flex items-center gap-1 osrs-num text-[#7CFC7C] text-sm" data-testid="guest-completed-count">
                <CheckCircle2 size={15} /> {completed} done
              </span>
              {(state.streak || 0) > 0 && (
                <span className="inline-flex items-center gap-1 osrs-num text-[#FF9A3A] text-sm" data-testid="guest-streak" title="Daily streak — keep it alive!">
                  <Flame size={15} className="text-[#FF7A1A]" /> {state.streak}d
                </span>
              )}
            </div>
          </div>
          <p className="osrs-shadow text-[#b9a06a] text-sm mb-5">
            Combat {state.combat_level} · Total level {state.total_level} · <span className="uppercase text-[10px]">{state.gamemode}</span>
          </p>

          {/* Task master card */}
          {state.current ? (
            <div className="bg-[#241b10] border-2 border-black p-4 mb-4" data-testid="guest-task">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 grid place-items-center bg-[#2e2416] border-2 border-[#4b3a1e]">
                  <Icon size={20} className="text-[#7CFC7C]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="osrs-shadow text-[#b9a06a] text-[10px] uppercase">{state.current.category}</span>
                    {state.current.tier && <span className="osrs-shadow text-[#b9a06a] text-[10px] uppercase">· {state.current.tier}</span>}
                    <span className="osrs-num text-[#7CFC7C] text-xs">+{state.current.points} pts</span>
                  </div>
                  <p className="osrs-num text-[#FFCF3F] text-base leading-tight mt-1" data-testid="guest-task-title">{state.current.title}</p>
                  <p className="osrs-shadow text-[#b9a06a] text-sm mt-1">{state.current.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <button data-testid="guest-verify-btn" onClick={verify} disabled={busy}
                  className="osrs-btn osrs-btn-green col-span-2 py-2.5 flex items-center justify-center gap-1">
                  <CheckCircle2 size={16} /> {busy ? "Checking..." : "Verify & Complete"}
                </button>
                <button data-testid="guest-skip-btn" onClick={skip} disabled={busy}
                  className="osrs-btn py-2.5 flex items-center justify-center gap-1">
                  <SkipForward size={15} /> Skip
                </button>
              </div>
              <p className="osrs-shadow text-[#8a7a54] text-[11px] mt-2 text-center">
                Do the task in-game, then hit Verify — we check your live Hiscores instantly.
              </p>
            </div>
          ) : (
            <div className="bg-[#241b10] border-2 border-black p-6 text-center mb-4" data-testid="guest-empty">
              <Sparkles size={22} className="text-[#FFCF3F] mx-auto mb-2" />
              <p className="osrs-shadow text-[#E6A519] text-sm">
                You've cleared every free guest task! Create an account to unlock <span className="text-[#FFCF3F]">quests, diaries, collection log</span> and the full board.
              </p>
            </div>
          )}

          {/* Conversion CTA */}
          <button data-testid="guest-signup-cta" onClick={goSignup}
            className="osrs-btn osrs-btn-green w-full py-3 flex items-center justify-center gap-2">
            <Coins size={16} />
            {state.points > 0 ? `Save & bank my ${state.points} points — create free account →` : "Create my free account →"}
          </button>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1 text-[#E6A519] osrs-shadow text-[11px]"><Flame size={13} className="text-[#FF9040]" /> Build a streak</span>
            <span className="inline-flex items-center gap-1 text-[#E6A519] osrs-shadow text-[11px]"><Trophy size={13} className="text-[#FFCF3F]" /> Monthly #1 wins a free Bond</span>
            <span className="inline-flex items-center gap-1 text-[#7CFC7C] osrs-shadow text-[11px]"><CheckCircle2 size={13} /> No email spam · delete anytime</span>
          </div>
        </div>

        {showExit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4" data-testid="guest-exit-modal" onClick={() => setShowExit(false)}>
            <div className="osrs-panel p-6 w-full max-w-md text-center" onClick={(e) => e.stopPropagation()}>
              <ShieldAlert size={30} className="text-[#FFCF3F] mx-auto mb-2" />
              <h3 className="osrs-title text-xl mb-2">Wait — your board vanishes!</h3>
              <p className="osrs-shadow text-[#E6A519] text-sm mb-5">
                You've earned <span className="osrs-num text-[#7CFC7C]">{state.points} points</span>
                {(state.streak || 0) > 0 && <> and a <span className="osrs-num text-[#FF9A3A]">{state.streak}-day streak</span></>} on
                <span className="osrs-num text-[#FFCF3F]"> {state.rsn}</span>. Create a free account in 5 seconds to keep it all — no email spam, delete anytime.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button data-testid="guest-exit-save" onClick={goSignup} className="osrs-btn osrs-btn-green px-5 py-2.5 text-sm flex items-center justify-center gap-1">
                  <Coins size={15} /> Save my {state.points} points →
                </button>
                <button data-testid="guest-exit-dismiss" onClick={() => setShowExit(false)} className="osrs-btn px-5 py-2.5 text-sm">
                  Keep playing as guest
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="w-full mt-6"><SiteFooter /></div>
      </div>
    </div>
  );
}
